import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { CaretDown, Check } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { Chip } from "@/components/ui/chip"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ScrollArea } from "@/components/ui/scroll-area"
import { SearchInput, SearchItem, SuggestionSearch } from "@/components/ui/search"
import { type AgentOption, useAgentOptions } from "@/hooks/useAgentOptions"
import { cn } from "@/lib/utils"
import { SearchOption } from "@/modules/patients/components/search-option"
import { useShiftPlanFiltersStore } from "@/modules/shift-plan-overview/stores/useShiftPlanFiltersStore"
import type { ShiftPlanRoleFilter, UserRole } from "@/types/__generated__/union-types"

interface AgentFilterProps {
  activeTab: ShiftPlanRoleFilter | string
}

const TAB_ROLES: Record<ShiftPlanRoleFilter | string, UserRole[]> = {
  DOCTORS_AND_MEDICAL_SUPERVISORS: ["DOCTOR", "MEDICAL_SUPERVISOR"],
  NURSING_PROFESSIONALS: ["NURSING_PROFESSIONAL"],
  CALL_CENTER_AGENTS: ["CALL_CENTER_AGENT"],
}

export const AgentFilter = ({ activeTab }: AgentFilterProps) => {
  const { t } = useTranslation()
  const [searchValue, setSearchValue] = useState("")

  const agents = useShiftPlanFiltersStore((state) => state.filters.agents)
  const setAgents = useShiftPlanFiltersStore((state) => state.actions.setAgents)
  const isLoading = useShiftPlanFiltersStore((state) => state.isLoading)
  const hasAgents = agents.length > 0

  const agentOptions = useAgentOptions()
  const allowedRoles = TAB_ROLES[activeTab]

  const roleFilteredOptions = useMemo(
    () => agentOptions.filter((option) => option.role !== null && allowedRoles.includes(option.role)),
    [agentOptions, allowedRoles],
  )

  const filteredOptions =
    searchValue.length < 3
      ? roleFilteredOptions
      : roleFilteredOptions.filter((option) => {
          const searchTerms = searchValue.trim().toLowerCase().split(" ")
          const label = option.label.toLowerCase()
          return searchTerms.some((term) => label.includes(term))
        })

  const hasResults = filteredOptions.length > 0

  const handleSelectOption = (option: AgentOption) => {
    const newAgents = agents.includes(option.value)
      ? agents.filter((agent) => agent !== option.value)
      : [...agents, option.value]

    setAgents(newAgents)
  }

  const handleReset = () => {
    setAgents([])
    setSearchValue("")
  }

  const agentLabel = useMemo(() => {
    const selectedOptions = roleFilteredOptions.filter((option) => agents.includes(option.value))

    if (selectedOptions.length <= 2) return selectedOptions.map((option) => option.label).join(", ")

    return selectedOptions
      .slice(0, 2)
      .map((option) => option.label)
      .join(", ")
      .concat(", ...")
  }, [roleFilteredOptions, agents])

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        asChild
        disabled={isLoading}
        className={cn(
          "aria-checked:border-on-primary-surface aria-checked:bg-primary-surface aria-checked:text-on-primary-surface aria-checked:hover:enabled:bg-primary-surface-hovered aria-checked:focus:enabled:border-on-primary-surface outline-hidden hover:cursor-pointer",
          isLoading && "cursor-not-allowed!",
        )}>
        <Chip
          checked={hasAgents}
          className="data-[state=checked]:hover:bg-primary-surface-hovered flex h-8 items-center gap-0.5"
          data-testid="shift-plan-agent-filter-trigger">
          <Check className={cn("overflow-hidden transition-all duration-200", hasAgents ? "w-5" : "w-0")} size={20} />
          <div className="truncate px-1.5">
            {hasAgents ? `${agentLabel} (${agents.length})` : t("shiftPlanOverview.filters.agent")}
          </div>
          <CaretDown size={20} />
        </Chip>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="max-w-81 rounded-lg" align="start">
        <SuggestionSearch className="w-fit">
          <div className="p-2 pb-0">
            <SearchInput
              onKeyDown={(e) => {
                e.stopPropagation()
              }}
              data-testid="shift-plan-agent-search-input"
              value={searchValue}
              placeholder={t("shiftPlanOverview.filters.searchAgent")}
              autoComplete="off"
              onChange={({ target: { value } }) => {
                setSearchValue(value)
              }}
              onClear={() => {
                setSearchValue("")
              }}
            />
            <DropdownMenuSeparator className="bg-border -mx-2 mt-2 mb-0" />
          </div>
          <div className="border-border max-h-80 border-b" data-testid="shift-plan-agent-search-section">
            {!hasResults && (
              <SearchItem
                disabled
                className="text-on-surface-subdued z-30 mx-2 cursor-default overflow-hidden rounded-md py-1.5">
                {t("common.nothingFound")}
              </SearchItem>
            )}
            <ScrollArea className="max-h-77.5">
              <div className="grid gap-0.5 first:mt-2 last:mb-2">
                {hasResults &&
                  filteredOptions.map((option) => {
                    const { value, label, icon, specialties } = option
                    return (
                      <div key={value}>
                        <SearchOption
                          checked={agents.includes(value)}
                          searchValue={searchValue}
                          value={value}
                          title={label}
                          onSelect={() => handleSelectOption(option)}
                          icon={icon}
                          className="mx-2 px-2 py-1"
                          description={specialties}
                        />
                      </div>
                    )
                  })}
              </div>
            </ScrollArea>
          </div>
          <DropdownMenuSeparator className="m-0" />
          <div className="flex justify-end">
            <div className="p-2">
              <Button
                onClick={handleReset}
                data-testid="shift-plan-agent-reset-button"
                variant="tertiary"
                className="h-10 rounded-full px-2 py-0.5 leading-5 font-medium">
                {t("common.button.reset")}
              </Button>
            </div>
          </div>
        </SuggestionSearch>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
